"use client";

import { gql } from "graphql-request";
import { ArrowRight, BarChart2, Check, CreditCard, Key, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { sectionReveal } from "@/components/ui/reveal-section";
import { useAuth } from "@/lib/auth";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";
import { useUsageSummary } from "@/lib/usage/usage.hooks";
import { cn } from "@/lib/utils";

export const ONBOARDING_BANNER_DISMISSED_KEY = "radium_onboarding_banner_dismissed";

const PERIOD_30D = { preset: "last_30d" } as const;

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const apiKeysDocument = gql`
  query OnboardingBannerApiKeys {
    apiKeys {
      id
    }
  }
`;

type ApiKeysQuery = { apiKeys: Array<{ id: string }> };

// ─── Step definitions ─────────────────────────────────────────────────────────

interface OnboardingStep {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  action: string;
  href: string;
  completed: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Progress + checklist for the dashboard welcome panel. Hides entirely when
 * dismissed (same localStorage key as the legacy full-width banner).
 */
export const OnboardingGettingStartedTrack = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const { user } = useAuth();

  const { data: apiKeysData } = useGraphqlQuery<ApiKeysQuery>({
    queryKey: ["onboarding-banner-api-keys"],
    document: apiKeysDocument,
    retry: false,
    enabled: !!user,
  });

  const { data: usageSummaryData } = useUsageSummary(PERIOD_30D, { enabled: !!user });

  const hasApiKey = (apiKeysData?.apiKeys?.length ?? 0) > 0;
  const hasUsage = Number(usageSummaryData?.usage_summary?.requests ?? 0) > 0;

  const steps: OnboardingStep[] = [
    {
      id: "api-key",
      icon: Key,
      title: "Get your API key",
      description: "Create a key to authenticate requests to the Radium API",
      action: "Create API key",
      href: "/deploy/keys",
      completed: hasApiKey,
    },
    {
      id: "payment-method",
      icon: CreditCard,
      title: "Add a payment method",
      description: "Add a payment method to keep your account active and avoid interruptions",
      action: "Add payment method",
      href: "/manage/billing",
      completed: false,
    },
    {
      id: "view-usage",
      icon: BarChart2,
      title: "View your usage",
      description: "Monitor token consumption, costs, and request volume over time",
      action: "View analytics",
      href: "/analytics",
      completed: hasUsage,
    },
  ];

  const completedCount = steps.filter((s) => s.completed).length;
  const progressPct = (completedCount / steps.length) * 100;

  useEffect(() => {
    const dismissed = localStorage.getItem(ONBOARDING_BANNER_DISMISSED_KEY);
    if (!dismissed) {
      setIsVisible(true);
      const timer = setTimeout(() => setAnimationStep(1), 50);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setAnimationStep(2);
    localStorage.setItem(ONBOARDING_BANNER_DISMISSED_KEY, "1");
    setTimeout(() => setIsVisible(false), 300);
  };

  return (
    <div
      className={cn(
        "mt-5 border-t border-border pt-5",
        sectionReveal.transition,
        (animationStep === 0 || animationStep === 2) && sectionReveal.hidden,
        animationStep === 1 && sectionReveal.visible,
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="font-medium text-muted-foreground">Getting started</span>
            <span className="shrink-0 font-semibold tabular-nums text-foreground">
              {completedCount} of {steps.length}
            </span>
          </div>
          <Progress value={progressPct} className="h-1.5" />
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Dismiss getting started guide</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {steps.map((step, index) => {
          const Icon = step.icon;

          return (
            <Link
              key={step.id}
              href={step.href}
              className="group relative flex h-full flex-col border border-border bg-background p-4 transition-colors hover:bg-background/80"
            >
              <div className="flex h-full flex-col">
                <div className="mb-4 flex items-start gap-3">
                  <div className="relative shrink-0">
                    <div
                      className={cn(
                        "flex h-9 w-9 items-center justify-center rounded-lg bg-secondary transition-colors",
                        step.completed
                          ? "text-green-600"
                          : "text-foreground group-hover:bg-primary group-hover:text-primary-foreground",
                      )}
                    >
                      {step.completed ? (
                        <Check className="h-4 w-4" aria-hidden />
                      ) : (
                        <Icon className="h-4 w-4" aria-hidden />
                      )}
                    </div>
                    {!step.completed && (
                      <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                        {index + 1}
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h4
                      className={cn(
                        "mb-0.5 text-sm font-semibold leading-tight",
                        step.completed ? "text-muted-foreground" : "text-foreground",
                      )}
                    >
                      {step.title}
                    </h4>
                    <p
                      className={cn(
                        "text-xs leading-relaxed",
                        step.completed ? "text-muted-foreground/60" : "text-muted-foreground",
                      )}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>

                <div
                  className={cn(
                    "mt-auto flex items-center justify-between text-xs font-medium",
                    step.completed ? "text-muted-foreground" : "text-primary",
                  )}
                >
                  <span>{step.completed ? "Done" : step.action}</span>
                  <ArrowRight className="h-3 w-3" aria-hidden />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
